import classNames from 'classnames'
import { Phone as PhoneIcon } from 'iconoir-react'
import { capitalize, compact } from 'lodash'
import moment from 'moment'
import { ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import Calendar from 'react-calendar'
import Combo from 'app/components/ui/combo/combo'
import Spinner from '../Spinner'
import { MobileOverlay } from './MobileOverlay'
import { SchedulingWizardContext } from './SchedulingWizard'
import { TimeSlot } from './TimeSlot'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { LocationJson } from 'app/types/entities'
import { OptionType } from 'types/OptionType'

const ANY_PROVIDER: OptionType = { id: ``, name: `Any`, value: { displayName: `Any` } }

export function AppointmentConfig({
  location,
  startDate,
}: {
  location: LocationJson
  startDate: string | undefined
}): ReactElement {
  const {
    appointmentTypes,
    apptType,
    back,
    patientType,
    provider,
    providers,
    selectedDaySlotIndex,
    selectedTimeSlot,
    daySlots,
    onMonthChange,
    setApptType,
    setFlowMode,
    setProvider,
    setSelectedTimeSlot,
    setSelectedAvailability,
    setSelectedDaySlotIndex,
    isExternalSlots,
    onDayClick,
    carestackAvailableDates,
    onCsMonthChange,
  } = useContext(SchedulingWizardContext)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [selectedProvider, setSelectedProvider] = useState<OptionType>(ANY_PROVIDER)
  const [isLoading, setIsLoading] = useState<boolean>(!startDate)
  const [calendarActiveDate, setCalendarActiveDate] = useState<Date>(() =>
    startDate ? moment(startDate).toDate() : new Date(),
  )
  const appointmentTypeOptions = appointmentTypes
    .filter((type) => !type.patientType || type.patientType === patientType)
    ?.map((apptType) => ({
      id: apptType.id!,
      value: apptType,
      name: apptType.alias ?? apptType.displayName!,
    }))
  const selectedAppointmentType = useMemo(
    () => appointmentTypeOptions.find((option) => option.id === apptType?.id),
    [apptType],
  )

  const providerOptions = [
    ANY_PROVIDER,
    ...providers.map((provider) => ({
      id: provider.id!,
      value: provider,
      name: capitalize(provider.displayName!),
    })),
  ]

  useEffect(() => {
    setFlowMode(`regular`)
  }, [])

  // Reflect an externally set provider in the dropdown. The public flow only sets
  // `provider` via this dropdown's onChange (which already updates selectedProvider),
  // but the in-app flow preselects the patient's preferred provider through the wizard
  // context — keep the Combo in sync so that preselection is visible. The functional
  // updater no-ops when the selection already matches, so the public flow doesn't incur
  // a redundant re-render on every provider change.
  useEffect(() => {
    setSelectedProvider((prev) => {
      const nextId = provider?.id ?? ANY_PROVIDER.id
      if (nextId === prev.id) return prev
      return provider ? { id: provider.id!, value: provider, name: capitalize(provider.displayName!) } : ANY_PROVIDER
    })
  }, [provider])

  // Sync calendar view and reset loading when startDate changes (new data has loaded)
  useEffect(() => {
    if (startDate) {
      setCalendarActiveDate(moment(startDate).toDate())
      setIsLoading(false)
    }
  }, [startDate])

  // External PMS (CareStack / Ascend): show loading while month availability is being fetched
  useEffect(() => {
    if (isExternalSlots && apptType && !carestackAvailableDates) {
      setIsLoading(true)
    } else if (isExternalSlots && carestackAvailableDates) {
      setIsLoading(false)
    }
  }, [isExternalSlots, apptType, carestackAvailableDates])

  return (
    <div
      className={`grid h-full grid-cols-1 gap-y-6 overflow-hidden animate-duration-[400ms] md:grid-cols-[60%_40%] md:grid-rows-[auto_auto_1fr] ${
        back ? `animate-fade-right` : `animate-fade-left`
      }`}
    >
      {isLoading && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .schedule-calendar .react-calendar__viewContainer {
                display: none !important;
              }
            `,
          }}
        />
      )}
      <div className="mr-0 hidden flex-col gap-y-1.5 md:mr-6 md:flex">
        <span className="text-sm font-medium text-default">Appointment type</span>
        <Combo
          options={appointmentTypeOptions}
          value={selectedAppointmentType}
          onChange={(option) => {
            setApptType(option.value)
            setSelectedTimeSlot(undefined)
            setSelectedAvailability?.(undefined)
            if (provider) {
              setProvider(undefined)
              setSelectedProvider(ANY_PROVIDER)
            }
          }}
          optionsContainerClassName="!normal-case"
          inputClassName="!py-3.5 !px-4 text-body-sm text-strong"
        />
      </div>
      <div className="hidden flex-col gap-y-1.5 md:flex">
        <span className="text-sm font-medium text-default">Provider</span>
        <Combo
          options={providerOptions}
          value={{
            id: selectedProvider.id,
            name: selectedProvider.value.displayName,
            value: selectedProvider,
          }}
          onChange={(option) => {
            if (!option?.id || option.id !== provider?.id) {
              setSelectedAvailability?.(undefined)
              setIsOpen(false)
            }
            setProvider(option.id === ANY_PROVIDER.id ? undefined : option.value)
            setSelectedTimeSlot(undefined)
            setSelectedProvider(option)
          }}
          inputClassName="!py-3.5 !px-4 text-body-sm text-strong"
        />
      </div>
      <div className="mr-0 mt-0 flex flex-col md:mr-6">
        <span className="hidden text-ui-sm text-strong md:flex">Select a date and time</span>
        <div className="flex flex-col gap-4">
          <Calendar
            key={carestackAvailableDates?.join(`,`) || `no-dates`}
            goToRangeStartOnSelect={false}
            defaultView="month"
            maxDetail="month"
            minDetail="month"
            minDate={new Date()}
            next2Label={null}
            prev2Label={null}
            className="schedule-calendar flex flex-col gap-4"
            formatShortWeekday={(locale, date) => moment(date).format(`dd`).charAt(0)}
            tileClassName={({ date }) =>
              classNames(
                `aspect-square rounded-lg text-center text-sm font-medium leading-none hover:bg-blue-600 hover:text-on-color disabled:rounded-lg disabled:bg-surface disabled:text-minimal`,
                {
                  [`underline underline-offset-4`]: moment(date).startOf(`d`).isSame(moment().startOf(`d`)),
                  'md:!bg-primary-600 md:!text-on-color !bg-surface-muted !text-strong':
                    selectedDaySlotIndex >= 0
                      ? moment(date)
                          .startOf(`d`)
                          .isSame(moment(startDate).add(selectedDaySlotIndex, `days`).startOf(`d`))
                      : false,
                  'bg-surface-muted text-strong': !moment(date)
                    .startOf(`d`)
                    .isSame(moment(startDate).add(selectedDaySlotIndex, `days`).startOf(`d`)),
                },
              )
            }
            onChange={(date) => {
              if (!(date instanceof Date)) return
              const dateStr = moment(date).format(`YYYY-MM-DD`)
              const selectedDaySlotIndex = isExternalSlots
                ? (daySlots?.findIndex((s) => s.date === dateStr) ?? -1)
                : moment(date).startOf(`day`).diff(moment(startDate).startOf(`day`), `days`)
              setSelectedDaySlotIndex(selectedDaySlotIndex)
              const timeSlot = daySlots?.[selectedDaySlotIndex]?.timeSlots?.find(
                (timeSlot) => timeSlot.time === selectedTimeSlot?.time && compact(timeSlot?.availabilities).length,
              )
              setSelectedTimeSlot(timeSlot)
              setIsOpen(true)

              if (isExternalSlots && onDayClick) {
                onDayClick(dateStr)
              }
            }}
            selectRange={false}
            calendarType="gregory"
            activeStartDate={calendarActiveDate}
            onActiveStartDateChange={({ activeStartDate: newDate }) => {
              if (!newDate) return
              const monthChanged =
                !calendarActiveDate ||
                moment(newDate).format(`YYYY-MM`) !== moment(calendarActiveDate).format(`YYYY-MM`)
              setCalendarActiveDate(newDate)
              if (monthChanged) {
                onMonthChange(moment(newDate).format(`YYYY-MM-DD`))
                if (onCsMonthChange) {
                  onCsMonthChange(moment(newDate).format(`YYYY-MM`))
                }
                setIsLoading(true)
                setSelectedDaySlotIndex(-1)
              }
            }}
            value={selectedDaySlotIndex >= 0 ? moment(startDate).add(selectedDaySlotIndex, `days`).toDate() : undefined}
            tileDisabled={({ date }) => {
              if (moment(date).startOf(`day`).isBefore(moment().startOf(`day`))) {
                return true
              }
              if (isExternalSlots && carestackAvailableDates) {
                return !carestackAvailableDates.includes(moment(date).format(`YYYY-MM-DD`))
              }
              return !daySlots?.[moment(date).startOf(`day`).diff(moment(startDate).startOf(`day`), `days`)]?.timeSlots
                ?.length
            }}
          />
          {!isLoading && (location.primaryTrackingNumber || location.officeMainNumber || location.businessPhone) && (
            <div className="mt-5 flex flex-col items-center gap-y-3 px-6 py-3">
              <span className="shrink-0 text-heading-sm text-strong md:text-ui-md">
                Can't find your preferred time?
              </span>
              <a
                href={`tel:${location.primaryTrackingNumber || location.officeMainNumber || location.businessPhone}`}
                className="flex w-full items-center justify-center gap-x-2 rounded-lg border border-primary-600 px-6 py-3 shadow-elevation-1 hover:border-primary-700 hover:bg-blue-500/[0.04] md:w-fit md:grow-0"
              >
                <PhoneIcon height={16} width={16} className="text-primary-600" />
                <span className="text-sm font-medium leading-tight text-primary-600">
                  {formatPhoneNumber(
                    location.primaryTrackingNumber || location.officeMainNumber || location.businessPhone,
                  )}
                </span>
              </a>
            </div>
          )}
        </div>
      </div>

      <div className={`relative hidden flex-col gap-y-6 overflow-hidden md:flex ${isLoading ? `invisible` : ``}`}>
        <TimeSlot location={location} startDate={startDate!} />
      </div>

      {isLoading && (
        <div className="flex flex-col items-center justify-start gap-2 py-4 md:col-span-2 md:py-8">
          <Spinner size={16} />
          <span className="text-sm text-subtle">Loading availability...</span>
        </div>
      )}

      {!isLoading && (
        <MobileOverlay closeLabel="Select a time" open={isOpen} onClose={() => setIsOpen(false)}>
          <TimeSlot location={location} startDate={startDate!} />
        </MobileOverlay>
      )}
    </div>
  )
}
